<script>
import BarChart from 'shared/components/charts/BarChart.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportsAPI from 'dashboard/api/reports';

export default {
  name: 'LabelExtraCharts',
  components: { BarChart, V4Button },
  props: {
    from: { type: Number, required: true },
    to: { type: Number, required: true },
    businessHours: { type: Boolean, default: false },
    groupBy: { type: Object, required: true },
    selectedFilter: { type: Object, required: true },
    type: { type: String, default: 'label' },
  },
  data() {
    return {
      byHour: { labels: [], datasets: [] },
      byDate: { labels: [], datasets: [] },
      byWeekday: { labels: [], datasets: [] },
      isLoading: false,
    };
  },
  mounted() {
    this.refresh();
  },
  watch: {
    from: 'refresh',
    to: 'refresh',
    businessHours: 'refresh',
    selectedFilter: 'refresh',
  },
  methods: {
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', e => reject(e));
          if (existing.readyState === 'complete') resolve();
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = e => reject(e);
        document.body.appendChild(s);
      });
    },
    async refresh() {
      if (!this.selectedFilter?.id) return;
      this.isLoading = true;
      try {
        // byDate
        const series = await ReportsAPI.getReports({
          metric: 'conversations_count',
          from: this.from,
          to: this.to,
          type: 'label',
          id: this.selectedFilter.id,
          groupBy: this.groupBy?.period || 'day',
          businessHours: this.businessHours,
        });
        const dateLabels = series.data.map(p => new Date(p.timestamp * 1000).toLocaleDateString());
        this.byDate = {
          labels: dateLabels,
          datasets: [
            { type: 'bar', backgroundColor: 'rgb(31, 147, 255)', label: this.$t('LABEL_EXTRA.TICKETS_BY_DATE'), data: series.data.map(p => p.value) },
          ],
        };

        // byHour (group_by hour)
        const hourSeries = await ReportsAPI.getReports({
          metric: 'conversations_count',
          from: this.from,
          to: this.to,
          type: 'label',
          id: this.selectedFilter.id,
          groupBy: 'hour',
          businessHours: this.businessHours,
        });
        // timestamps are hourly bins; convert hour of day
        const hourBuckets = new Array(24).fill(0);
        hourSeries.data.forEach(p => {
          const d = new Date(p.timestamp * 1000);
          const h = d.getHours();
          hourBuckets[h] += p.value;
        });
        this.byHour = {
          labels: [...Array(24).keys()].map(h => h.toString()),
          datasets: [
            { type: 'bar', backgroundColor: '#5BA21D', label: this.$t('LABEL_EXTRA.TICKETS_BY_HOUR'), data: hourBuckets },
          ],
        };

        // byWeekday
        const weekdayBuckets = new Array(7).fill(0);
        let weekdayCounts = new Array(7).fill(0);
        series.data.forEach(p => {
          const d = new Date(p.timestamp * 1000);
          const w = d.getDay();
          weekdayBuckets[w] += p.value;
          weekdayCounts[w] += 1;
        });
        // average per weekday
        const avgWeekday = weekdayBuckets.map((sum, i) => (weekdayCounts[i] ? sum / weekdayCounts[i] : 0));
        const weekdayLabels = [
          this.$t('DAYS_OF_WEEK.SUNDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.MONDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.TUESDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.WEDNESDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.THURSDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.FRIDAY').slice(0, 3),
          this.$t('DAYS_OF_WEEK.SATURDAY').slice(0, 3),
        ];
        this.byWeekday = {
          labels: weekdayLabels,
          datasets: [
            { type: 'bar', backgroundColor: '#0F766E', label: this.$t('LABEL_EXTRA.AVG_TICKETS_BY_WEEKDAY'), data: avgWeekday },
          ],
        };
      } finally {
        this.isLoading = false;
      }
    },
    async downloadPdf() {
      // Load libraries from CDN to avoid bundling dependencies
      await this.loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
      await this.loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
      const { jsPDF } = window.jspdf || {};
      const html2canvas = window.html2canvas;
      if (!jsPDF || !html2canvas) return;

      const doc = new jsPDF('p', 'pt', 'a4');
      const node = this.$refs.wrapper;
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
      const imgWidth = canvas.width * ratio;
      const imgHeight = canvas.height * ratio;
      const x = (pageWidth - imgWidth) / 2;
      const y = 20;
      doc.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST');
      const fileName = `label-report-${this.selectedFilter?.title || 'all'}.pdf`;
      doc.save(fileName);
    },
  },
};
</script>

<template>
  <div ref="wrapper" class="flex flex-col gap-6 px-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="h-72 p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2">
        <div class="text-sm font-medium mb-2">{{ $t('LABEL_EXTRA.TITLES.TICKETS_BY_DATE') }}</div>
        <BarChart :collection="byDate" />
      </div>
      <div class="h-72 p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2">
        <div class="text-sm font-medium mb-2">{{ $t('LABEL_EXTRA.TITLES.TICKETS_BY_HOUR') }}</div>
        <BarChart :collection="byHour" />
      </div>
      <div class="h-72 p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2">
        <div class="text-sm font-medium mb-2">{{ $t('LABEL_EXTRA.TITLES.AVG_TICKETS_BY_WEEKDAY') }}</div>
        <BarChart :collection="byWeekday" />
      </div>
    </div>
  </div>
</template>


